import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Plus, Pencil, Trash2, Loader2, ChevronDown,
  ChevronUp, Save, X, AlertTriangle, RefreshCw, CheckCircle,
  TrendingUp, BookOpen, Users, MapPin
} from 'lucide-react';
import api from '../../utils/api';

/* ── Shared input styles ───────────────────────────────────────────────────── */
const inp = "w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all bg-white";
const lbl = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";

/* ── Section wrapper ────────────────────────────────────────────────────────── */
const Section = ({ title, subtitle, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-royal-50 rounded-xl flex items-center justify-center">
            <Icon size={18} className="text-royal-800" />
          </div>
          <div className="text-left">
            <div className="font-display font-semibold text-gray-900">{title}</div>
            {subtitle && <div className="text-xs text-gray-400 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {open ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
      </button>
      {open && <div className="border-t border-gray-100 p-5">{children}</div>}
    </div>
  );
};

/* ── Delete confirm modal ────────────────────────────────────────────────────── */
const DeleteModal = ({ onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Trash2 size={22} className="text-red-600" />
      </div>
      <h3 className="font-display font-semibold text-gray-900 mb-2">Delete this item?</h3>
      <p className="text-gray-500 text-sm mb-5">This cannot be undone.</p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
        <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-colors">Delete</button>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════════════
   METRICS SECTION
══════════════════════════════════════════════════════════════════════════════ */
const MetricsSection = () => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [msg, setMsg] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setMetrics((await api.get('/analytics/admin/metrics')).data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openEdit = (m) => { setEditing(m._id || 'new'); setForm(m._id ? { ...m } : { key:'', label:'', value:0, suffix:'+', prefix:'', delta:'', description:'', group:'headline', order:0, isPublished:true }); setMsg(''); };
  const save = async () => {
    setSaving(true); setMsg('');
    try {
      if (editing === 'new') await api.post('/analytics/admin/metrics', form);
      else await api.put(`/analytics/admin/metrics/${editing}`, form);
      setMsg('success'); fetch();
      setTimeout(() => { setEditing(null); setMsg(''); }, 900);
    } catch (e) { setMsg('error:' + (e.response?.data?.message || 'Save failed')); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    await api.delete(`/analytics/admin/metrics/${id}`);
    setDeleting(null); fetch();
  };

  const GROUPS = ['headline','secondary'];

  return (
    <>
      {deleting && <DeleteModal onConfirm={() => del(deleting)} onCancel={() => setDeleting(null)} />}
      <div className="flex justify-end mb-4">
        <button onClick={() => openEdit({})} className="btn-royal text-sm py-2 px-4 flex items-center gap-2"><Plus size={15}/>Add Metric</button>
      </div>

      {editing && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">{editing === 'new' ? 'New Metric' : 'Edit Metric'}</h4>
            <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-200 rounded-lg"><X size={16}/></button>
          </div>
          {msg.startsWith('error:') && <div className="mb-3 text-red-600 text-sm flex items-center gap-2"><AlertTriangle size={14}/>{msg.slice(6)}</div>}
          {msg === 'success' && <div className="mb-3 text-emerald-600 text-sm flex items-center gap-2"><CheckCircle size={14}/>Saved!</div>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Key (unique ID)*</label><input className={inp} placeholder="e.g. teachers_trained" value={form.key||''} onChange={e => setForm(p=>({...p,key:e.target.value}))}/></div>
            <div><label className={lbl}>Label*</label><input className={inp} placeholder="e.g. Educators Trained" value={form.label||''} onChange={e => setForm(p=>({...p,label:e.target.value}))}/></div>
            <div><label className={lbl}>Value*</label><input type="number" className={inp} value={form.value??0} onChange={e => setForm(p=>({...p,value:+e.target.value}))}/></div>
            <div><label className={lbl}>Suffix</label><input className={inp} placeholder="+ or %" value={form.suffix||''} onChange={e => setForm(p=>({...p,suffix:e.target.value}))}/></div>
            <div><label className={lbl}>Prefix</label><input className={inp} placeholder="₦ etc" value={form.prefix||''} onChange={e => setForm(p=>({...p,prefix:e.target.value}))}/></div>
            <div><label className={lbl}>Delta (YoY)</label><input className={inp} placeholder="22%" value={form.delta||''} onChange={e => setForm(p=>({...p,delta:e.target.value}))}/></div>
            <div><label className={lbl}>Group</label>
              <select className={inp} value={form.group||'headline'} onChange={e => setForm(p=>({...p,group:e.target.value}))}>
                {GROUPS.map(g=><option key={g}>{g}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Order</label><input type="number" className={inp} value={form.order??0} onChange={e => setForm(p=>({...p,order:+e.target.value}))}/></div>
            <div className="col-span-2"><label className={lbl}>Description</label><input className={inp} placeholder="Short description shown below value" value={form.description||''} onChange={e => setForm(p=>({...p,description:e.target.value}))}/></div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="mpub" checked={!!form.isPublished} onChange={e=>setForm(p=>({...p,isPublished:e.target.checked}))} className="accent-royal-800 w-4 h-4"/>
              <label htmlFor="mpub" className="text-sm text-gray-600 cursor-pointer">Published (visible on website)</label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 bg-royal-800 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-royal-800"/></div> : (
        <div className="divide-y divide-gray-100">
          {metrics.map(m => (
            <div key={m._id} className="flex items-center gap-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-sm text-gray-900">{m.label}</span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[10px] font-semibold capitalize">{m.group}</span>
                  {!m.isPublished && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">Hidden</span>}
                </div>
                <div className="text-xs text-gray-400 font-mono">{m.key} · Value: <strong className="text-royal-800">{m.prefix}{m.value.toLocaleString()}{m.suffix}</strong>{m.delta && ` · ↑${m.delta}`}</div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => openEdit(m)} className="p-2 text-gray-400 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors"><Pencil size={14}/></button>
                <button onClick={() => setDeleting(m._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {metrics.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No metrics yet. Add your first metric above.</p>}
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   DATASETS SECTION
══════════════════════════════════════════════════════════════════════════════ */
const DatasetForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || {
    key:'', label:'', description:'', chartType:'bar', category:'education',
    seriesLabels:['Value'], seriesColors:['#2B3A8F'], dataPoints:[], isPublished:true, order:0,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const setField = (k, v) => setForm(p => ({...p, [k]: v}));

  const addPoint = () => setForm(p => ({...p, dataPoints:[...p.dataPoints, { label:'', value:0, value2:null, value3:null, color:'' }]}));
  const removePoint = (i) => setForm(p => ({...p, dataPoints: p.dataPoints.filter((_,idx)=>idx!==i)}));
  const updatePoint = (i, field, val) => setForm(p => ({...p, dataPoints: p.dataPoints.map((pt, idx) => idx===i ? {...pt, [field]: val} : pt)}));
  const updateSeries = (arr, field) => setField(field, arr);

  const save = async () => {
    setSaving(true); setMsg('');
    try { await onSave(form); }
    catch (e) { setMsg('error:' + (e.response?.data?.message || 'Save failed')); setSaving(false); }
  };

  const CHART_TYPES = ['bar','horizontal_bar','line','area','pie'];
  const CATEGORIES  = ['education','research','community','assessment','other'];

  const numSeries = (() => {
    if (['pie'].includes(form.chartType)) return 1;
    if (form.dataPoints.some(p => p.value3 != null && p.value3 !== '')) return 3;
    if (form.dataPoints.some(p => p.value2 != null && p.value2 !== '')) return 2;
    return 1;
  })();

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
      {msg.startsWith('error:') && <div className="mb-3 text-red-600 text-sm flex items-center gap-2"><AlertTriangle size={14}/>{msg.slice(6)}</div>}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div><label className={lbl}>Key (unique)*</label><input className={inp} placeholder="e.g. teacher_training_by_year" value={form.key} onChange={e=>setField('key',e.target.value)}/></div>
        <div><label className={lbl}>Label*</label><input className={inp} placeholder="Chart title" value={form.label} onChange={e=>setField('label',e.target.value)}/></div>
        <div><label className={lbl}>Chart Type</label>
          <select className={inp} value={form.chartType} onChange={e=>setField('chartType',e.target.value)}>
            {CHART_TYPES.map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>)}
          </select>
        </div>
        <div><label className={lbl}>Category (filter tab)</label>
          <select className={inp} value={form.category} onChange={e=>setField('category',e.target.value)}>
            {CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
        </div>
        <div><label className={lbl}>Order</label><input type="number" className={inp} value={form.order} onChange={e=>setField('order',+e.target.value)}/></div>
        <div className="flex items-center gap-2 mt-5">
          <input type="checkbox" checked={!!form.isPublished} onChange={e=>setField('isPublished',e.target.checked)} className="accent-royal-800 w-4 h-4"/>
          <span className="text-sm text-gray-600">Published</span>
        </div>
        <div className="col-span-2"><label className={lbl}>Description</label><input className={inp} value={form.description} onChange={e=>setField('description',e.target.value)}/></div>
      </div>

      {/* Series config */}
      <div className="mb-4 p-4 bg-white border border-gray-100 rounded-xl">
        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Series Labels & Colors</div>
        <div className="space-y-2">
          {[0,1,2].map(i => (
            form.chartType !== 'pie' || i === 0 ? (
              <div key={i} className="flex gap-2 items-center">
                <span className="text-xs text-gray-400 w-16 flex-shrink-0">Series {i+1}</span>
                <input className={`${inp} flex-1`} placeholder={`Series ${i+1} label`}
                  value={form.seriesLabels?.[i]||''} onChange={e=>{ const arr=[...(form.seriesLabels||[])]; arr[i]=e.target.value; updateSeries(arr,'seriesLabels'); }}/>
                <input type="color" value={form.seriesColors?.[i]||'#2B3A8F'}
                  onChange={e=>{ const arr=[...(form.seriesColors||[])]; arr[i]=e.target.value; updateSeries(arr,'seriesColors'); }}
                  className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-1 flex-shrink-0"/>
              </div>
            ) : null
          ))}
        </div>
      </div>

      {/* Data points */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Data Points</div>
          <button onClick={addPoint} className="text-xs font-semibold text-royal-800 flex items-center gap-1 hover:underline"><Plus size={12}/>Add Row</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-3 text-gray-400 font-semibold">Label (X-axis)</th>
                <th className="text-left py-2 pr-3 text-gray-400 font-semibold">Value 1</th>
                {numSeries >= 2 && <th className="text-left py-2 pr-3 text-gray-400 font-semibold">Value 2</th>}
                {numSeries >= 3 && <th className="text-left py-2 pr-3 text-gray-400 font-semibold">Value 3</th>}
                {form.chartType === 'pie' && <th className="text-left py-2 pr-3 text-gray-400 font-semibold">Color</th>}
                <th/>
              </tr>
            </thead>
            <tbody>
              {form.dataPoints.map((pt, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="py-1.5 pr-2"><input className={`${inp} text-xs py-1.5`} placeholder="Label" value={pt.label||''} onChange={e=>updatePoint(i,'label',e.target.value)}/></td>
                  <td className="py-1.5 pr-2"><input type="number" className={`${inp} text-xs py-1.5`} value={pt.value??''} onChange={e=>updatePoint(i,'value',+e.target.value)}/></td>
                  {numSeries >= 2 && <td className="py-1.5 pr-2"><input type="number" className={`${inp} text-xs py-1.5`} placeholder="—" value={pt.value2??''} onChange={e=>updatePoint(i,'value2',e.target.value===''?null:+e.target.value)}/></td>}
                  {numSeries >= 3 && <td className="py-1.5 pr-2"><input type="number" className={`${inp} text-xs py-1.5`} placeholder="—" value={pt.value3??''} onChange={e=>updatePoint(i,'value3',e.target.value===''?null:+e.target.value)}/></td>}
                  {form.chartType === 'pie' && <td className="py-1.5 pr-2"><input type="color" value={pt.color||'#2B3A8F'} onChange={e=>updatePoint(i,'color',e.target.value)} className="w-9 h-9 rounded-lg border border-gray-200 cursor-pointer p-1"/></td>}
                  <td className="py-1.5"><button onClick={()=>removePoint(i)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><X size={12}/></button></td>
                </tr>
              ))}
              {form.dataPoints.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-gray-400">No data points yet. Click "Add Row" to begin.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {numSeries < 3 && form.chartType !== 'pie' && (
          <button onClick={addPoint} className="mt-2 text-xs text-royal-800 hover:underline flex items-center gap-1"><Plus size={11}/>Add another row</button>
        )}
        {numSeries < 2 && form.chartType !== 'pie' && form.dataPoints.length > 0 && (
          <p className="text-[10px] text-gray-400 mt-1">💡 Fill in Value 2 on any row to enable a second data series (e.g. Nigeria)</p>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onCancel} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
        <button onClick={save} disabled={saving} className="px-5 py-2 bg-royal-800 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save Dataset
        </button>
      </div>
    </div>
  );
};

const DatasetsSection = () => {
  const [datasets, setDatasets] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null); // null | 'new' | id
  const [deleting, setDeleting] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setDatasets((await api.get('/analytics/admin/datasets')).data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async (form) => {
    if (editing === 'new') await api.post('/analytics/admin/datasets', form);
    else await api.put(`/analytics/admin/datasets/${editing}`, form);
    setEditing(null); fetch();
  };
  const del = async (id) => { await api.delete(`/analytics/admin/datasets/${id}`); setDeleting(null); fetch(); };

  return (
    <>
      {deleting && <DeleteModal onConfirm={()=>del(deleting)} onCancel={()=>setDeleting(null)}/>}
      <div className="flex justify-end mb-4">
        <button onClick={()=>setEditing('new')} className="btn-royal text-sm py-2 px-4 flex items-center gap-2"><Plus size={15}/>Add Dataset</button>
      </div>
      {editing && (
        <div className="mb-5">
          <DatasetForm
            initial={editing !== 'new' ? datasets.find(d=>d._id===editing) : null}
            onSave={save}
            onCancel={()=>setEditing(null)}
          />
        </div>
      )}
      {loading ? <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-royal-800"/></div> : (
        <div className="space-y-2">
          {datasets.map(d => (
            <div key={d._id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="font-semibold text-sm text-gray-900">{d.label}</span>
                  <span className="px-2 py-0.5 bg-royal-100 text-royal-700 rounded-full text-[10px] font-semibold capitalize">{d.chartType?.replace('_',' ')}</span>
                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-[10px] font-semibold capitalize">{d.category}</span>
                  {!d.isPublished && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">Hidden</span>}
                </div>
                <div className="text-xs text-gray-400">{d.dataPoints?.length || 0} data points · {d.seriesLabels?.join(', ')}</div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={()=>setEditing(d._id)} className="p-2 text-gray-400 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors"><Pencil size={14}/></button>
                <button onClick={()=>setDeleting(d._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {datasets.length===0 && <p className="text-gray-400 text-sm text-center py-8">No datasets yet.</p>}
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   CASE STUDIES SECTION
══════════════════════════════════════════════════════════════════════════════ */
const CaseStudiesSection = () => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/analytics/admin/case-studies')).data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const DEPTS = ['research','capacity','innovation','grants'];

  const openEdit = (item) => {
    setEditing(item._id || 'new');
    setForm(item._id ? { ...item, outcomes: item.outcomes || [] } : { title:'', department:'research', location:'', year:'', outcomes:[], isPublished:true, order:0 });
  };
  const save = async () => {
    setSaving(true);
    try {
      if (editing==='new') await api.post('/analytics/admin/case-studies', form);
      else await api.put(`/analytics/admin/case-studies/${editing}`, form);
      setEditing(null); fetch();
    } catch(e) { alert(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/analytics/admin/case-studies/${id}`); setDeleting(null); fetch(); };

  const addOutcome = () => setForm(p => ({...p, outcomes:[...p.outcomes,'']}));
  const updateOutcome = (i,v) => setForm(p => ({...p, outcomes:p.outcomes.map((o,idx)=>idx===i?v:o)}));
  const removeOutcome = (i) => setForm(p => ({...p, outcomes:p.outcomes.filter((_,idx)=>idx!==i)}));

  return (
    <>
      {deleting && <DeleteModal onConfirm={()=>del(deleting)} onCancel={()=>setDeleting(null)}/>}
      <div className="flex justify-end mb-4">
        <button onClick={()=>openEdit({})} className="btn-royal text-sm py-2 px-4 flex items-center gap-2"><Plus size={15}/>Add Case Study</button>
      </div>
      {editing && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-5">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-gray-900">{editing==='new'?'New Case Study':'Edit Case Study'}</h4>
            <button onClick={()=>setEditing(null)} className="p-1 hover:bg-gray-200 rounded-lg"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="col-span-2"><label className={lbl}>Title*</label><input className={inp} value={form.title||''} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></div>
            <div><label className={lbl}>Department</label>
              <select className={inp} value={form.department||'research'} onChange={e=>setForm(p=>({...p,department:e.target.value}))}>
                {DEPTS.map(d=><option key={d}>{d}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Year</label><input className={inp} placeholder="2023" value={form.year||''} onChange={e=>setForm(p=>({...p,year:e.target.value}))}/></div>
            <div className="col-span-2"><label className={lbl}>Location</label><input className={inp} placeholder="Lagos & Abuja, Nigeria" value={form.location||''} onChange={e=>setForm(p=>({...p,location:e.target.value}))}/></div>
            <div><label className={lbl}>Order</label><input type="number" className={inp} value={form.order??0} onChange={e=>setForm(p=>({...p,order:+e.target.value}))}/></div>
            <div className="flex items-center gap-2 mt-5">
              <input type="checkbox" checked={!!form.isPublished} onChange={e=>setForm(p=>({...p,isPublished:e.target.checked}))} className="accent-royal-800 w-4 h-4"/>
              <span className="text-sm text-gray-600">Published</span>
            </div>
          </div>
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <label className={lbl}>Outcomes / Key Results</label>
              <button onClick={addOutcome} className="text-xs font-semibold text-royal-800 flex items-center gap-1 hover:underline"><Plus size={12}/>Add</button>
            </div>
            <div className="space-y-2">
              {(form.outcomes||[]).map((o,i)=>(
                <div key={i} className="flex gap-2">
                  <input className={`${inp} flex-1`} placeholder="e.g. 400 teachers trained" value={o} onChange={e=>updateOutcome(i,e.target.value)}/>
                  <button onClick={()=>removeOutcome(i)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg"><X size={14}/></button>
                </div>
              ))}
              {(form.outcomes||[]).length===0 && <p className="text-xs text-gray-400">No outcomes yet. Click Add to begin.</p>}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={()=>setEditing(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 bg-royal-800 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
              {saving?<Loader2 size={14} className="animate-spin"/>:<Save size={14}/>} Save
            </button>
          </div>
        </div>
      )}
      {loading ? <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-royal-800"/></div> : (
        <div className="space-y-2">
          {items.map(item=>(
            <div key={item._id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5"><span className="font-semibold text-sm text-gray-900">{item.title}</span>{!item.isPublished&&<span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">Hidden</span>}</div>
                <div className="text-xs text-gray-400 capitalize">{item.department} · {item.location} · {item.year} · {item.outcomes?.length||0} outcomes</div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={()=>openEdit(item)} className="p-2 text-gray-400 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors"><Pencil size={14}/></button>
                <button onClick={()=>setDeleting(item._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {items.length===0&&<p className="text-gray-400 text-sm text-center py-8">No case studies yet.</p>}
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   MILESTONES SECTION
══════════════════════════════════════════════════════════════════════════════ */
const MilestonesSection = () => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/analytics/admin/milestones')).data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const openEdit = (item) => {
    setEditing(item._id||'new');
    setForm(item._id ? {...item, tags:item.tags||[]} : {year:'', title:'', description:'', tags:[], isPublished:true, order:0});
  };
  const save = async () => {
    setSaving(true);
    try {
      if (editing==='new') await api.post('/analytics/admin/milestones', form);
      else await api.put(`/analytics/admin/milestones/${editing}`, form);
      setEditing(null); fetch();
    } catch(e) { alert(e.response?.data?.message||'Save failed'); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/analytics/admin/milestones/${id}`); setDeleting(null); fetch(); };

  return (
    <>
      {deleting && <DeleteModal onConfirm={()=>del(deleting)} onCancel={()=>setDeleting(null)}/>}
      <div className="flex justify-end mb-4">
        <button onClick={()=>openEdit({})} className="btn-royal text-sm py-2 px-4 flex items-center gap-2"><Plus size={15}/>Add Milestone</button>
      </div>
      {editing && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-5">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-gray-900">{editing==='new'?'New Milestone':'Edit Milestone'}</h4>
            <button onClick={()=>setEditing(null)} className="p-1 hover:bg-gray-200 rounded-lg"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Year*</label><input className={inp} placeholder="2024" value={form.year||''} onChange={e=>setForm(p=>({...p,year:e.target.value}))}/></div>
            <div><label className={lbl}>Order</label><input type="number" className={inp} value={form.order??0} onChange={e=>setForm(p=>({...p,order:+e.target.value}))}/></div>
            <div className="col-span-2"><label className={lbl}>Title*</label><input className={inp} value={form.title||''} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></div>
            <div className="col-span-2"><label className={lbl}>Description*</label><textarea rows={3} className={`${inp} resize-none`} value={form.description||''} onChange={e=>setForm(p=>({...p,description:e.target.value}))}/></div>
            <div className="col-span-2"><label className={lbl}>Tags (comma-separated)</label><input className={inp} placeholder="AI, Innovation, Nigeria" value={(form.tags||[]).join(', ')} onChange={e=>setForm(p=>({...p,tags:e.target.value.split(',').map(t=>t.trim()).filter(Boolean)}))}/></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.isPublished} onChange={e=>setForm(p=>({...p,isPublished:e.target.checked}))} className="accent-royal-800 w-4 h-4"/>
              <span className="text-sm text-gray-600">Published</span>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={()=>setEditing(null)} className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 bg-royal-800 text-white rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
              {saving?<Loader2 size={14} className="animate-spin"/>:<Save size={14}/>} Save
            </button>
          </div>
        </div>
      )}
      {loading ? <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-royal-800"/></div> : (
        <div className="space-y-2">
          {items.map(item=>(
            <div key={item._id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="w-16 flex-shrink-0 text-center">
                <div className="font-display font-bold text-royal-800 text-lg">{item.year}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5"><span className="font-semibold text-sm text-gray-900">{item.title}</span>{!item.isPublished&&<span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">Hidden</span>}</div>
                <div className="text-xs text-gray-500 line-clamp-1">{item.description}</div>
                {item.tags?.length > 0 && <div className="flex gap-1 mt-1 flex-wrap">{item.tags.map(t=><span key={t} className="px-1.5 py-0.5 bg-gold-100 text-gold-700 rounded text-[10px] font-semibold">{t}</span>)}</div>}
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={()=>openEdit(item)} className="p-2 text-gray-400 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors"><Pencil size={14}/></button>
                <button onClick={()=>setDeleting(item._id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {items.length===0&&<p className="text-gray-400 text-sm text-center py-8">No milestones yet.</p>}
        </div>
      )}
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════════════════════ */
export default function AdminAnalytics() {
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');

  const seed = async () => {
    setSeeding(true); setSeedMsg('');
    try {
      await api.get('/analytics/seed');
      setSeedMsg('success');
      setTimeout(()=>setSeedMsg(''),3000);
      window.location.reload();
    } catch(e) { setSeedMsg('error:'+e.message); }
    finally { setSeeding(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 size={22} className="text-royal-800"/> Analytics & Impact Data
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Manage all data shown on the Impact Dashboard and Insights Portal.</p>
        </div>
        <div className="flex gap-2 items-center">
          {seedMsg === 'success' && <span className="text-emerald-600 text-sm flex items-center gap-1"><CheckCircle size={14}/>Seeded!</span>}
          {seedMsg.startsWith('error:') && <span className="text-red-600 text-sm">{seedMsg.slice(6)}</span>}
          <button onClick={seed} disabled={seeding}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-60">
            {seeding ? <Loader2 size={15} className="animate-spin"/> : <RefreshCw size={15}/>}
            Seed Default Data
          </button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/>
        <div>
          <strong>First time?</strong> Click "Seed Default Data" to populate all sections with SAAI's sample data. You can then edit everything here.
          The seed button will only add missing records — it won't overwrite existing ones.
        </div>
      </div>

      <Section title="Impact Metrics" subtitle="Numbers shown on the Impact Dashboard counters and KPI strips" icon={TrendingUp} defaultOpen>
        <MetricsSection/>
      </Section>

      <Section title="Chart Datasets" subtitle="Data for all charts on the Insights Portal — bar, line, area, pie, and horizontal bar charts" icon={BarChart3}>
        <DatasetsSection/>
      </Section>

      <Section title="Case Studies" subtitle="Active and completed project summaries shown on the Impact Dashboard" icon={BookOpen}>
        <CaseStudiesSection/>
      </Section>

      <Section title="Milestones Timeline" subtitle="Key institutional milestones shown on the Impact Dashboard timeline" icon={MapPin}>
        <MilestonesSection/>
      </Section>
    </div>
  );
}